import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Plus, Download, Trash2, Clock, CheckCircle, XCircle, Calendar, Video } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/LoadingSpinner';
import TimelapseAPI from '../../services/timelapseApi';

const TimelapsePage = () => {
  const queryClient = useQueryClient();
  const api = new TimelapseAPI();

  const { data: jobs, isLoading, error, refetch } = useQuery(
    'timelapse-jobs', 
    () => api.getAllJobs(),
    { 
      refetchInterval: 5000, // Refresh every 5 seconds
      onError: (error) => {
        console.error('Failed to load timelapse jobs:', error);
      }
    }
  );

  const deleteMutation = useMutation((jobId) => api.deleteJob(jobId), {
    onSuccess: () => {
      queryClient.invalidateQueries('timelapse-jobs');
      toast.success('Timelapse job deleted successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete timelapse job');
    }
  });

  const handleDelete = (jobId) => {
    if (window.confirm('Are you sure you want to delete this timelapse job? This action cannot be undone.')) {
      deleteMutation.mutate(jobId);
    }
  };

  const handleDownload = (jobId, jobName) => {
    try {
      const downloadUrl = api.getDownloadUrl(jobId);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${jobName || 'timelapse'}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download timelapse');
    }
  };


  const getStatusIcon = (status) => {
    switch (status) {
      case 'queued':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'processing':
        return <div className="h-5 w-5 loading-spinner border-2"></div>;
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'queued':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <LoadingSpinner className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load timelapse jobs. Please try again.</p>
          <button
            onClick={() => refetch()}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Timelapse Jobs</h1>
          <p className="mt-2 text-gray-600">View and manage your timelapse video jobs</p>
        </div>
      </div>

      {/* Jobs List */}
      {jobs && jobs.length > 0 ? (
        <div className="space-y-4">
          {jobs.map((job) => (
            <div key={job.id} className="card p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {getStatusIcon(job.status)}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {job.name || `${job.camera} Timelapse`}
                    </h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                      <span>Camera: {job.camera}</span>
                      <span>
                        {format(new Date(job.startDate), 'MMM d, yyyy')} - {format(new Date(job.endDate), 'MMM d, yyyy')}
                      </span>
                      <span>{job.fps} FPS</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                        {job.status}
                      </span>
                    </div>
                    {job.progress !== undefined && job.status === 'processing' && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${job.progress}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Progress: {job.progress}% ({job.processedImages || 0} / {job.totalImages || 0} images)
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
                  {job.status === 'completed' && (
                    <>
                      <button
                        onClick={() => handleDownload(job.id, job.name)}
                        className="btn btn-success flex items-center justify-center text-sm min-w-0 px-2"
                        title="Download Video"
                      >
                        <Download className="h-4 w-4 mr-1 flex-shrink-0" />
                        <span className="truncate">Download</span>
                      </button>
                    </>
                  )}
                  
                  {(job.status === 'completed' || job.status === 'failed') && (
                    <button
                      onClick={() => handleDelete(job.id)}
                      className="btn btn-danger flex items-center justify-center text-sm min-w-0 px-2"
                      title="Delete Job"
                    >
                      <Trash2 className="h-4 w-4 mr-1 flex-shrink-0" />
                      <span className="truncate">Delete</span>
                    </button>
                  )}
                </div>
              </div>

              {job.error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm">{job.error}</p>
                </div>
              )}

              <div className="mt-4 text-sm text-gray-500">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  Created {format(new Date(job.createdAt), 'MMM d, yyyy HH:mm')}
                  {job.startedAt && (
                    <span className="ml-4">
                      Started {format(new Date(job.startedAt), 'MMM d, yyyy HH:mm')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Video className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No timelapse jobs yet</h3>
          <p className="text-gray-500 mb-6">
            Create timelapses from your camera photos to see them here
          </p>
        </div>
      )}

    </div>
  );
};

export default TimelapsePage;
